import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { createAccDocument, ensureOpenItemLinks, updateAccDocument } from '../../../services/mutations';
import {
  Badge,
  Button,
  Card,
  DateInput,
  EyeIcon,
  CoinIcon,
  LinkChainIcon,
  ListTreeIcon,
  NumberInput,
  Select,
  TextInput,
  TrashIcon,
  WizardIcon,
} from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { isApOpenItemAccount, isBankLikeAccount } from '../shared/accountTypeUtils';
import { JournalBalanceBar } from '../shared/JournalBalanceBar';
import {
  applyDocumentLinePatch,
  isBalanced,
  localToUsdAmount,
  perCompanyBalances,
  sumDebitCredit,
  sumDebitCreditUsd,
  toNumber,
} from '../shared/balanceUtils';
import { mapLinesToPayload, decodeOpenBalanceNote } from '../shared/documentLinePayload';
import { DocumentLineForm, newDocumentLine } from '../shared/documentLineTypes';
import { buildLineFromLinkSelection, resolveRateForCurrency } from '../shared/linkPickerLineUtils';
import {
  normalizePickerRecords,
  pickOldestRecordsUntilAmount,
  toLinkPickerSelection,
  type LinkPickerResponse,
  type LinkPickerSelection,
} from '../shared/linkPickerNormalize';
import { OpenItemLinkPickerModal } from '../shared/OpenItemLinkPickerModal';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { useAccDocument } from '../documents/useData';
import { moduleConfig } from '../documents/config';
import {
  allocateBankAmountToLines,
  invoiceLineKey,
  isOverPayLine,
  refreshPaymentInvoiceOpenBalances,
  remainingAfterPay,
  resolvePaySettlementStatus,
  round2,
  settlementStatusLabel,
} from './paymentAllocation';
import {
  PaymentAmountBlock,
  PaymentEmptyInvoices,
  PaymentField,
  PaymentInvoicePanelHeader,
  PaymentPanelTitle,
  PaymentReconcileStrip,
  PaymentStatusRow,
} from './PaymentFormUi';

type Lookup = {
  id: number;
  code: string;
  name: string;
  combLevel?: string;
  allowOpenItems?: boolean;
  currencyIds?: number[];
  companyIds?: number[];
  accountType?: { id: number; code: string; name: string } | null;
  operationalType?: { id: number; code: string; name: string } | null;
  effectiveOperationalType?: { id: number; code: string; name: string } | null;
  /** @deprecated */
  effectiveAccountType?: { id: number; code: string; name: string } | null;
};

const accountSelectLabel = (a: Lookup) => a.name || a.combLevel || a.code;

type DocumentTypeLookup = { id: number; code: string; name: string; isActive?: boolean };

const todayIso = () => new Date().toISOString().slice(0, 10);

const sumCreditLocal = (lines: DocumentLineForm[]) =>
  round2(lines.reduce((sum, line) => sum + toNumber(line.creditLocal), 0));

const activeInvoiceLines = (lines: DocumentLineForm[]) =>
  lines.filter((l) => toNumber(l.creditLocal) > 0.005);

export const PaymentForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack('/accounting/journals', getLocationReturnPath(location.state));
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const editId = id && id !== 'new' ? Number(id) : 0;
  const { item, isLoading } = useAccDocument(editId);
  const rateCacheRef = useRef(new Map<string, { rate: number | null; rateDate: string | null }>());
  const bankAmountTouchedRef = useRef(false);

  const [documentTypeId, setDocumentTypeId] = useState('');
  const [documentDate, setDocumentDate] = useState(todayIso);
  const [postingDate, setPostingDate] = useState(todayIso);
  const [description, setDescription] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [apAccountId, setApAccountId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [rate, setRate] = useState('');
  const [bankAmountLocal, setBankAmountLocal] = useState('');
  const [bankAmountUsd, setBankAmountUsd] = useState('');
  const [invoiceLines, setInvoiceLines] = useState<DocumentLineForm[]>([]);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fillingOldest, setFillingOldest] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const { data: companiesRaw } = useSWR<Lookup[] | { data: Lookup[] }>('/acc-companies', fetcher);
  const { data: accountsRaw } = useSWR<Lookup[] | { data: Lookup[] }>('/acc-accounts?postingOnly=true', fetcher);
  const { data: currencies } = useSWR<{ id: number; code: string; name: string }[]>('/currencies', fetcher);
  const { data: documentTypesRaw } = useSWR<DocumentTypeLookup[] | { data: DocumentTypeLookup[] }>(
    '/acc-document-types',
    fetcher,
  );

  const companies = Array.isArray(companiesRaw) ? companiesRaw : companiesRaw?.data ?? [];
  const accounts = Array.isArray(accountsRaw) ? accountsRaw : accountsRaw?.data ?? [];
  const documentTypes = Array.isArray(documentTypesRaw) ? documentTypesRaw : documentTypesRaw?.data ?? [];

  const companyOptions = companies.map((c) => ({ value: String(c.id), label: c.name }));
  const apAccounts = useMemo(
    () => accounts
      .filter(isApOpenItemAccount)
      .sort((a, b) => (a.combLevel ?? a.code).localeCompare(b.combLevel ?? b.code)),
    [accounts],
  );
  const bankAccounts = useMemo(() => {
    const typed = accounts.filter(isBankLikeAccount);
    // Until COA is tagged BANK/CASH, keep previous broad list so the form remains usable.
    const list = typed.length > 0
      ? typed
      : accounts.filter((a) => !a.allowOpenItems);
    return list.sort((a, b) => (a.combLevel ?? a.code).localeCompare(b.combLevel ?? b.code));
  }, [accounts]);
  const apOptions = useMemo(
    () => [{ value: '', label: 'Select AP account…' }, ...apAccounts.map((a) => ({ value: String(a.id), label: accountSelectLabel(a) }))],
    [apAccounts],
  );
  const bankOptions = useMemo(
    () => [{ value: '', label: 'Select bank account…' }, ...bankAccounts.map((a) => ({ value: String(a.id), label: accountSelectLabel(a) }))],
    [bankAccounts],
  );
  const currencyOptions = useMemo(() => (currencies ?? []).map((c) => ({ value: String(c.id), label: c.name || c.code })), [currencies]);

  const accountMeta = useMemo(() => {
    const m: Record<string, { currencyIds?: number[]; companyIds?: number[] }> = {};
    for (const a of accounts) {
      m[String(a.id)] = { currencyIds: a.currencyIds, companyIds: a.companyIds };
    }
    return m;
  }, [accounts]);

  const currenciesForBank = useMemo(() => {
    const allowed = accountMeta[bankAccountId]?.currencyIds ?? [];
    if (!allowed.length) return currencyOptions;
    return currencyOptions.filter((c) => allowed.includes(Number(c.value)));
  }, [accountMeta, bankAccountId, currencyOptions]);

  const companyNames = useMemo(() => {
    const m: Record<number, string> = {};
    for (const c of companies) m[c.id] = `${c.name}`;
    return m;
  }, [companies]);

  const invoiceTotal = useMemo(() => sumCreditLocal(activeInvoiceLines(invoiceLines)), [invoiceLines]);
  const invoiceUsdTotal = useMemo(
    () => round2(activeInvoiceLines(invoiceLines).reduce((sum, line) => sum + toNumber(line.creditUSD), 0)),
    [invoiceLines],
  );
  const hasOverPay = useMemo(() => invoiceLines.some(isOverPayLine), [invoiceLines]);
  const payableInvoiceLines = useMemo(() => activeInvoiceLines(invoiceLines), [invoiceLines]);

  const bankLine = useMemo((): DocumentLineForm => {
    const base = applyDocumentLinePatch(newDocumentLine(), {
      companyId,
      accountId: bankAccountId,
      currencyId,
      rate,
      debitLocal: bankAmountLocal,
      creditLocal: '',
    });
    return base;
  }, [bankAccountId, bankAmountLocal, companyId, currencyId, rate]);

  const allLines = useMemo(() => {
    if (!bankAccountId || !companyId || !bankAmountLocal) return payableInvoiceLines;
    return [bankLine, ...payableInvoiceLines];
  }, [bankAccountId, bankAmountLocal, bankLine, companyId, payableInvoiceLines]);

  const balanceLines = allLines
    .filter((l) => l.companyId)
    .map((l) => ({ companyId: Number(l.companyId), debitLocal: l.debitLocal, creditLocal: l.creditLocal }));
  const usdBalanceLines = allLines
    .filter((l) => l.companyId)
    .map((l) => ({ debitUSD: l.debitUSD, creditUSD: l.creditUSD }));
  const totals = sumDebitCredit(balanceLines);
  const usdTotals = sumDebitCreditUsd(usdBalanceLines);
  const companyTotals = perCompanyBalances(balanceLines);
  const balanced = isBalanced(balanceLines);
  const amountMatch = payableInvoiceLines.length === 0
    ? false
    : Math.abs(toNumber(bankAmountLocal) - invoiceTotal) <= 0.01;
  const canSave = balanced && amountMatch && !hasOverPay && payableInvoiceLines.length > 0;

  const currencyCode = currencyOptions.find((o) => o.value === currencyId)?.label ?? '';
  const partialCount = invoiceLines.filter((l) => resolvePaySettlementStatus(l) === 'partial').length;
  const fullCount = invoiceLines.filter((l) => resolvePaySettlementStatus(l) === 'full').length;
  const canPickInvoices = Boolean(companyId && apAccountId);

  useEffect(() => {
    const paymentType = documentTypes.find((t) => t.code === 'PAYMENT');
    if (paymentType) setDocumentTypeId(String(paymentType.id));
  }, [documentTypes]);

  useEffect(() => {
    rateCacheRef.current.clear();
  }, [postingDate]);

  useEffect(() => {
    if (!item || hydrated) return;
    if (item.documentType?.code && item.documentType.code !== 'PAYMENT') {
      navigate(moduleConfig.module.route.edit(item.id));
      return;
    }
    if (item.status !== 'DRAFT' && item.status !== 'TEMP') {
      navigate(moduleConfig.module.route.detail(item.id));
      return;
    }

    setDocumentDate(item.documentDate?.slice(0, 10) ?? todayIso());
    setPostingDate(item.postingDate?.slice(0, 10) ?? todayIso());
    setDescription(item.description ?? '');
    setDocumentTypeId(String(item.documentTypeId));

    const lines = item.lines ?? [];
    const bank = lines.find((l) => !l.account?.allowOpenItems && toNumber(l.debitLocal) > 0)
      ?? lines.find((l) => !l.account?.allowOpenItems);
    const invoices = lines.filter((l) => l.account?.allowOpenItems && (toNumber(l.creditLocal) > 0 || l.linkNumber));

    if (bank) {
      setCompanyId(String(bank.companyId));
      setBankAccountId(String(bank.accountId));
      setCurrencyId(bank.currencyId ? String(bank.currencyId) : '');
      setRate(bank.rate != null ? String(bank.rate) : '');
      setBankAmountLocal(bank.debitLocal != null ? round2(Number(bank.debitLocal)).toFixed(2) : '');
      setBankAmountUsd(bank.debitUSD != null ? round2(Number(bank.debitUSD)).toFixed(2) : '');
    }
    if (invoices[0]) {
      setApAccountId(String(invoices[0].accountId));
    }
    setInvoiceLines(
      invoices.map((l) => {
        const { openBalance: storedOpen, notes } = decodeOpenBalanceNote(l.notes);
        const credit = l.creditLocal != null ? round2(Number(l.creditLocal)).toFixed(2) : '';
        return {
          key: `line-${l.id}`,
          companyId: String(l.companyId),
          accountId: String(l.accountId),
          debitLocal: '',
          creditLocal: credit,
          currencyId: l.currencyId ? String(l.currencyId) : '',
          rate: l.rate != null ? String(l.rate) : '',
          debitUSD: '',
          creditUSD: l.creditUSD != null ? round2(Number(l.creditUSD)).toFixed(2) : '',
          narrative: l.narrative ?? '',
          plPeriodFrom: l.plPeriodFrom?.slice(0, 10) ?? '',
          plPeriodTo: l.plPeriodTo?.slice(0, 10) ?? '',
          dn: l.dn ?? '',
          notes,
          linkNumber: l.linkNumber ?? '',
          dueDate: l.dueDate?.slice(0, 10) ?? '',
          openBalance: storedOpen || credit,
        };
      }),
    );
    bankAmountTouchedRef.current = true;
    setHydrated(true);
  }, [hydrated, item, navigate]);

  useEffect(() => {
    if (!hydrated || !companyId || !apAccountId || invoiceLines.length === 0) return;
    const links = invoiceLines.map((l) => l.linkNumber?.trim()).filter(Boolean);
    if (links.length === 0) return;

    const url = `/accounting/open-items/link-picker?companyId=${companyId}&accountId=${apAccountId}&asOfDate=${postingDate || todayIso()}`;
    void fetcher(url).then((data: LinkPickerResponse) => {
      const rows = normalizePickerRecords(data);
      setInvoiceLines((prev) => refreshPaymentInvoiceOpenBalances(prev, rows));
    }).catch(() => {
      // Keep fallback openBalance from hydration.
    });
  // Intentionally omit invoiceLines from deps — run once when form context is ready after hydrate
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, companyId, apAccountId, postingDate]);

  useEffect(() => {
    if (bankAmountTouchedRef.current || invoiceLines.length === 0) return;
    const active = activeInvoiceLines(invoiceLines);
    const total = sumCreditLocal(active);
    if (total <= 0) return;
    setBankAmountLocal(String(total));
    setBankAmountUsd(String(round2(active.reduce((s, l) => s + toNumber(l.creditUSD), 0))));
  }, [invoiceLines]);

  const handleCurrencyChange = async (nextCurrencyId: string) => {
    setCurrencyId(nextCurrencyId);
    if (!nextCurrencyId) {
      setRate('');
      setBankAmountUsd('');
      return;
    }
    const resolved = await resolveRateForCurrency(
      nextCurrencyId,
      postingDate,
      rateCacheRef.current,
    );
    setRate(resolved);
    if (bankAmountLocal && resolved) {
      setBankAmountUsd(String(localToUsdAmount(bankAmountLocal, resolved)));
    }
  };

  const handleBankAmountChange = (value: string) => {
    bankAmountTouchedRef.current = true;
    setBankAmountLocal(value);
    setBankAmountUsd(value ? String(localToUsdAmount(value, rate)) : '');
  };

  const handleAllocateBankToInvoices = () => {
    const bank = toNumber(bankAmountLocal);
    if (bank <= 0.005) {
      toast.error('Enter a bank amount before allocating');
      return;
    }
    if (invoiceLines.length === 0) {
      toast.error('Select invoices first');
      return;
    }
    bankAmountTouchedRef.current = true;
    setInvoiceLines((prev) => allocateBankAmountToLines(prev, bank));
    toast.success('Bank amount allocated to invoices (oldest due first)');
  };

  const applySelectionsAsInvoiceLines = useCallback(async (
    selections: LinkPickerSelection[],
    options?: { replace?: boolean; allocateToBank?: boolean; keepBankAmount?: boolean },
  ) => {
    if (!companyId || !apAccountId || selections.length === 0) return;

    let resolvedSelections = selections;
    const needsLink = selections.filter((s) => !s.linkNumber?.trim());
    if (needsLink.length > 0) {
      try {
        const { assignments } = await ensureOpenItemLinks({
          companyId: Number(companyId),
          accountId: Number(apAccountId),
          lineIds: needsLink.map((s) => s.recordId),
        });
        const byLineId = new Map(assignments.map((a) => [a.lineId, a.linkNumber]));
        resolvedSelections = selections.map((s) => {
          const existing = s.linkNumber?.trim();
          if (existing) return s;
          const generated = byLineId.get(s.recordId);
          return generated ? { ...s, linkNumber: generated } : s;
        });
        const generatedCount = assignments.filter((a) => a.generated).length;
        if (generatedCount > 0) {
          toast.success(
            generatedCount === 1
              ? 'Link # auto-generated for invoice without a link'
              : `Link # auto-generated for ${generatedCount} invoices without a link`,
          );
        }
      } catch {
        toast.error('Could not auto-generate Link # for unlinked invoices');
        return;
      }
    }

    const ctx = {
      postingDate,
      currencies: currencyOptions,
      rateCache: rateCacheRef.current,
    };
    const built = await Promise.all(
      resolvedSelections.map((selection) =>
        buildLineFromLinkSelection(
          {
            ...newDocumentLine(),
            companyId,
            accountId: apAccountId,
          },
          selection,
          ctx,
        ),
      ),
    );

    const bank = toNumber(bankAmountLocal);
    const allocateToBank = Boolean(options?.allocateToBank && bank > 0.005);
    const keepBankAmount = Boolean(options?.keepBankAmount && bank > 0.005);

    setInvoiceLines((prev) => {
      let next: DocumentLineForm[];
      if (options?.replace) {
        next = built;
      } else {
        const byKey = new Map(prev.map((l) => [invoiceLineKey(l), l]));
        for (const line of built) {
          const key = invoiceLineKey(line);
          const existing = byKey.get(key);
          if (!existing) {
            byKey.set(key, line);
            continue;
          }
          // Same invoice re-selected: take the newer pay amount from picker
          byKey.set(key, {
            ...existing,
            ...line,
            key: existing.key,
            creditLocal: line.creditLocal,
            creditUSD: line.creditUSD,
            openBalance: line.openBalance || existing.openBalance,
          });
        }
        next = Array.from(byKey.values());
      }
      if (allocateToBank) {
        next = allocateBankAmountToLines(next, bank);
        if (options?.replace) {
          next = next.filter((l) => toNumber(l.creditLocal) > 0.005);
        }
      }
      return next;
    });

    if (allocateToBank || keepBankAmount) {
      // Keep user-entered bank outflow (do not overwrite from invoice sum).
      bankAmountTouchedRef.current = true;
    } else {
      bankAmountTouchedRef.current = false;
    }
  }, [apAccountId, bankAmountLocal, companyId, currencyOptions, postingDate]);

  const handleFillOldestUntilAmount = useCallback(async () => {
    const bank = toNumber(bankAmountLocal);
    if (bank <= 0.005) {
      toast.error('Enter the bank outflow amount first');
      return;
    }
    if (!companyId || !apAccountId) {
      toast.error('Select company and AP account first');
      return;
    }

    setFillingOldest(true);
    try {
      const url = `/accounting/open-items/link-picker?companyId=${companyId}&accountId=${apAccountId}&asOfDate=${postingDate || todayIso()}`;
      const data = await fetcher(url) as LinkPickerResponse;
      const records = normalizePickerRecords(data);
      const picked = pickOldestRecordsUntilAmount(records, bank);
      if (picked.length === 0) {
        toast.error('No open invoices found for this AP account');
        return;
      }
      await applySelectionsAsInvoiceLines(
        picked.map(({ record, payAmount }) => toLinkPickerSelection(
          { ...record, linkBalance: record.debitLocal > 0.005 ? record.debitLocal : record.linkBalance },
          payAmount,
        )),
        { replace: true, allocateToBank: false, keepBankAmount: true },
      );
      toast.success(
        `Selected ${picked.length} oldest invoice${picked.length === 1 ? '' : 's'} — full pays, last may be partial`,
      );
    } catch {
      toast.error('Could not load open invoices');
    } finally {
      setFillingOldest(false);
    }
  }, [apAccountId, applySelectionsAsInvoiceLines, bankAmountLocal, companyId, postingDate]);

  const updateInvoicePay = useCallback((key: string, value: string) => {
    setInvoiceLines((prev) =>
      prev.map((l) => (l.key === key ? applyDocumentLinePatch(l, { creditLocal: value }) : l)),
    );
  }, []);

  const handleRateChange = (value: string) => {
    setRate(value);
    if (bankAmountLocal) {
      setBankAmountUsd(String(localToUsdAmount(bankAmountLocal, value)));
    }
  };

  const removeInvoiceLine = (key: string) => {
    setInvoiceLines((prev) => prev.filter((l) => l.key !== key));
  };

  const handleLinkPickerSelect = useCallback(async (selections: LinkPickerSelection[]) => {
    const bank = toNumber(bankAmountLocal);
    await applySelectionsAsInvoiceLines(selections, {
      replace: invoiceLines.length === 0,
      allocateToBank: false,
      // Keep bank outflow when the user already entered it (e.g. target 500).
      keepBankAmount: bank > 0.005,
    });
    setLinkPickerOpen(false);
  }, [applySelectionsAsInvoiceLines, bankAmountLocal, invoiceLines.length]);

  const linkStatementUrl = useCallback((link: string) => {
    const params = new URLSearchParams({
      companyId,
      accountId: apAccountId,
      linkNumber: link,
      dateFrom: '2000-01-01',
      dateTo: postingDate || todayIso(),
    });
    return `/accounting/reports/link-statement?${params.toString()}`;
  }, [apAccountId, companyId, postingDate]);

  const renderSettlementBadge = (status: ReturnType<typeof resolvePaySettlementStatus>) => {
    const variant =
      status === 'full' ? 'success'
        : status === 'partial' ? 'warning'
          : status === 'skipped' ? 'secondary'
            : 'default';
    return (
      <Badge variant={variant} className="font-normal whitespace-nowrap">
        {settlementStatusLabel[status]}
      </Badge>
    );
  };

  const invoiceColumns = useMemo((): Column<DocumentLineForm>[] => [
    {
      key: 'linkNumber',
      header: 'Link',
      width: '5.5rem',
      render: (_v, row) => (
        row.linkNumber?.trim() ? (
          <div className="flex items-center gap-1">
            <Badge variant="info" className="font-normal tabular-nums">{row.linkNumber}</Badge>
            {companyId && apAccountId && (
              <Link
                to={linkStatementUrl(row.linkNumber.trim())}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(colors.primaryFg, colors.interactiveHover, 'rounded')}
                title="Open link statement (invoice + all payments on this link)"
                onClick={(e) => e.stopPropagation()}
              >
                <EyeIcon className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        ) : (
          <Badge variant="secondary" className="font-normal">No link</Badge>
        )
      ),
    },
    {
      key: 'narrative',
      header: 'Details',
      render: (_v, row) => (
        <span className="truncate block" title={row.narrative}>{row.narrative}</span>
      ),
    },
    {
      key: 'settlement',
      header: 'Settlement',
      width: '6.5rem',
      render: (_v, row) => renderSettlementBadge(resolvePaySettlementStatus(row)),
    },
    {
      key: 'openBalance',
      header: 'Outstanding',
      align: 'right',
      width: '6rem',
      render: (_v, row) => (
        <span className={cn('tabular-nums text-sm', colors.textMuted)}>
          {row.openBalance || '—'}
        </span>
      ),
    },
    {
      key: 'creditLocal',
      header: 'Pay now',
      align: 'right',
      width: '7rem',
      render: (_v, row) => (
        <NumberInput
          value={row.creditLocal}
          onChange={(e) => updateInvoicePay(row.key, String(e.target.value ?? ''))}
          className={cn(
            'text-right tabular-nums h-8 text-sm',
            isOverPayLine(row) && 'border-red-500 focus-visible:ring-red-500',
          )}
          allowDecimals
        />
      ),
    },
    {
      key: 'remaining',
      header: 'Left open',
      align: 'right',
      width: '6rem',
      render: (_v, row) => {
        const left = remainingAfterPay(row);
        const status = resolvePaySettlementStatus(row);
        return (
          <span
            className={cn(
              'tabular-nums text-sm font-medium',
              status === 'full' && (isDark ? 'text-emerald-400' : 'text-emerald-700'),
              status === 'partial' && (isDark ? 'text-amber-400' : 'text-amber-700'),
              status === 'skipped' && colors.textMuted,
            )}
          >
            {status === 'skipped' ? '—' : left.toFixed(2)}
          </span>
        );
      },
    },
    {
      key: 'creditUSD',
      header: 'Cr USD',
      align: 'right',
      width: '6rem',
      render: (_v, row) => <span className="tabular-nums text-sm">{row.creditUSD}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '2.5rem',
      render: (_v, row) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', isDark ? 'text-red-400' : 'text-red-600', colors.interactiveHover)}
          onClick={() => removeInvoiceLine(row.key)}
          aria-label="Remove"
        >
          <TrashIcon className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ], [apAccountId, colors.interactiveHover, colors.primaryFg, colors.textMuted, companyId, isDark, linkStatementUrl, updateInvoicePay]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentTypeId) {
      toast.error('Select a document type');
      return;
    }
    if (!companyId || !bankAccountId || !apAccountId) {
      toast.error('Company, AP account, and bank account are required');
      return;
    }
    if (invoiceLines.length === 0) {
      toast.error('Select at least one invoice to pay');
      return;
    }
    if (payableInvoiceLines.length === 0) {
      toast.error('Enter a pay amount on at least one invoice');
      return;
    }
    if (payableInvoiceLines.some((l) => !l.linkNumber?.trim())) {
      toast.error('Every invoice line needs a Link # (auto-generated when you select unlinked invoices)');
      return;
    }
    if (hasOverPay) {
      toast.error('One or more lines exceed the outstanding balance');
      return;
    }
    if (!balanced) {
      toast.error('Payment must be balanced');
      return;
    }
    if (!amountMatch) {
      toast.error('Bank amount must equal total pay amounts');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        documentTypeId: Number(documentTypeId),
        documentDate: documentDate || undefined,
        postingDate: postingDate || undefined,
        description: description || undefined,
        lines: mapLinesToPayload(allLines),
      };
      let docId = editId;
      if (editId) {
        await updateAccDocument(editId, payload);
      } else {
        const created = await createAccDocument(payload) as { id: number };
        docId = created.id;
      }
      toast.success('Payment saved');
      navigate(docId ? `/accounting/payments/${docId}/edit` : '/accounting/journals');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (editId && isLoading) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 h-full min-h-0 -mt-1">
      <form
        onSubmit={onSubmit}
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        {/* Header */}
        <div className={cn('flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b', colors.border, colors.headerBg)}>
          <div className="flex items-center gap-2 min-w-0">
            <Badge variant="info" className="font-normal text-[10px] uppercase tracking-wide">Draft</Badge>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={goBack}>Cancel</Button>
            {editId > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                title="Open full journal lines grid"
                onClick={() => navigate(`${moduleConfig.module.route.edit(editId)}?view=journal`)}
              >
                <ListTreeIcon className="h-3.5 w-3.5" />
                Journal format
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={!canSave || saving}
              loading={saving}
              className="min-w-[5.5rem]"
            >
              Save draft
            </Button>
          </div>
        </div>

        {/* Main — left / right */}
        <div className={cn('flex-1 overflow-y-auto dashboard-scrollbar min-h-0', colors.background)}>
          <div className="p-4 grid grid-cols-1 xl:grid-cols-12 gap-4 xl:min-h-full xl:items-stretch">
            {/* Left: bank source */}
            <div className="xl:col-span-4 xl:sticky xl:top-0 xl:self-start space-y-4">
              <Card className={cn('overflow-hidden shadow-sm', colors.border, colors.surface)}>
                <div className={cn('h-1', colors.primary)} />
                <div className="p-3 space-y-2.5">
                  <PaymentPanelTitle>Payment source</PaymentPanelTitle>

                  <div className="space-y-2">
                    <PaymentField label="Company">
                      <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} options={[{ value: '', label: 'Select company…' }, ...companyOptions]} />
                    </PaymentField>
                    <PaymentField label="Bank account">
                      <Select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)} options={bankOptions} />
                    </PaymentField>
                    <div className="grid grid-cols-2 gap-3">
                      <PaymentField label="Doc. date">
                        <DateInput value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} showFormatHint={false} />
                      </PaymentField>
                      <PaymentField label="Posting date">
                        <DateInput value={postingDate} onChange={(e) => setPostingDate(e.target.value)} showFormatHint={false} />
                      </PaymentField>
                    </div>
                    <PaymentField label="Description">
                      <TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Wire reference, note…" />
                    </PaymentField>
                  </div>

                  <PaymentAmountBlock
                    currencyCode={currencyCode}
                    bankAmountUsd={bankAmountUsd || '—'}
                    allocateDisabled={!bankAmountLocal || invoiceLines.length === 0}
                    onAllocate={handleAllocateBankToInvoices}
                    bankAmountLocal={(
                      <NumberInput
                        value={bankAmountLocal}
                        onChange={(e) => handleBankAmountChange(String(e.target.value ?? ''))}
                        className="text-right tabular-nums text-base font-semibold h-10 min-h-10"
                        allowDecimals
                      />
                    )}
                  >
                    <PaymentField label="Currency">
                      <Select
                        value={currencyId}
                        onChange={(e) => { void handleCurrencyChange(e.target.value); }}
                        options={[{ value: '', label: '—' }, ...currenciesForBank]}
                      />
                    </PaymentField>
                    <PaymentField label="Rate">
                      <NumberInput
                        value={rate}
                        onChange={(e) => handleRateChange(String(e.target.value ?? ''))}
                        className="text-right tabular-nums h-10 min-h-10"
                      />
                    </PaymentField>
                  </PaymentAmountBlock>

                  <PaymentStatusRow
                    canSave={canSave}
                    amountMatch={amountMatch}
                    balanced={balanced}
                    hasOverPay={hasOverPay}
                    partialCount={partialCount}
                    fullCount={fullCount}
                  />
                </div>
              </Card>
            </div>

            {/* Right: invoices */}
            <div className="xl:col-span-8 flex flex-col min-h-[18rem] xl:min-h-0 xl:h-full">
              <Card className={cn('flex flex-col flex-1 min-h-0 overflow-hidden shadow-sm', colors.border, colors.surface)}>
                <PaymentInvoicePanelHeader
                  invoiceCount={invoiceLines.length}
                  apSelect={(
                    <Select value={apAccountId} onChange={(e) => setApAccountId(e.target.value)} options={apOptions} />
                  )}
                  fillOldestButton={(
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'h-10 min-h-10 px-2.5 shrink-0 self-stretch',
                        colors.primaryFg,
                        colors.primaryBorderAll,
                        colors.interactiveHover,
                      )}
                      disabled={!canPickInvoices || fillingOldest || toNumber(bankAmountLocal) <= 0.005}
                      onClick={() => { void handleFillOldestUntilAmount(); }}
                      title="Pay oldest until amount"
                      aria-label="Pay oldest until amount"
                    >
                      {fillingOldest ? (
                        <span className="text-xs">…</span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5">
                          <WizardIcon className="h-4 w-4" />
                          <CoinIcon className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  )}
                  selectButton={(
                    <Button
                      type="button"
                      className="h-10 min-h-10 gap-1.5 shrink-0 self-stretch"
                      disabled={!canPickInvoices}
                      onClick={() => setLinkPickerOpen(true)}
                    >
                      <LinkChainIcon className="h-4 w-4" />
                      Select invoices
                    </Button>
                  )}
                />

                <div className="flex-1 min-h-0 overflow-auto">
                  {invoiceLines.length === 0 ? (
                    <PaymentEmptyInvoices />
                  ) : (
                    <DataTable
                      {...ACCOUNTING_TABLE_DEFAULTS}
                      data={invoiceLines}
                      columns={invoiceColumns}
                      keyExtractor={(row) => row.key}
                      density="compact"
                      containerClassName="border-0 shadow-none rounded-none"
                    />
                  )}
                </div>

                {invoiceLines.length > 0 && (
                  <PaymentReconcileStrip
                    payTotal={invoiceTotal}
                    bankTotal={toNumber(bankAmountLocal)}
                    usdTotal={invoiceUsdTotal}
                    matched={amountMatch}
                    invoiceCount={payableInvoiceLines.length}
                  />
                )}
              </Card>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 mt-auto">
          <JournalBalanceBar
            debitLocal={totals.debit}
            creditLocal={totals.credit}
            debitUSD={usdTotals.debit}
            creditUSD={usdTotals.credit}
            diff={totals.diff}
            balanced={balanced}
            companyTotals={companyTotals}
            companyNames={companyNames}
          />
        </div>
      </form>

      <OpenItemLinkPickerModal
        isOpen={linkPickerOpen}
        onClose={() => setLinkPickerOpen(false)}
        companyId={companyId ? Number(companyId) : null}
        accountId={apAccountId ? Number(apAccountId) : null}
        asOfDate={postingDate}
        targetAmount={toNumber(bankAmountLocal) > 0.005 ? toNumber(bankAmountLocal) : null}
        onSelect={(selections) => { void handleLinkPickerSelect(selections); }}
      />
    </div>
  );
};
